import { Modal } from "../../../utils/Modal";
import { detectCategoryFromFile, uploadSingleAttachmentFile } from "../../components/FileUploader";

interface CmsMediaOverlayCallbacks {
    onPick: () => void | Promise<void>;
    onRemove: () => void | Promise<void>;
    onDrop: (file: File) => void | Promise<void>;
}

export class CmsMediaFieldEditor {
    static async pick(): Promise<File | null> {
        return new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*,video/*";
            input.addEventListener("change", () => {
                resolve(input.files?.[0] ?? null);
                input.remove();
            }, { once: true });
            input.click();
        });
    }

    static async upload(file: File, uploadUrl: string) {
        const category = detectCategoryFromFile(file);
        if (category !== "image" && category !== "video") return null;
        return uploadSingleAttachmentFile(file, {
            uploadUrl,
            category,
            targetType: "temp",
            targetId: 0,
            metaRole: "cms_template_media",
        });
    }

    static setLoading(field: HTMLElement, loading: boolean, message = "업로드 중..."): void {
        const host = this.loaderHost(field);
        host.classList.toggle("is-cms-template-media-loading", loading);
        const existing = Array.from(host.children)
            .find((child): child is HTMLElement => child instanceof HTMLElement && child.classList.contains("admin-cms-template-media-loader"));
        if (!loading) {
            existing?.remove();
            return;
        }
        const loader = existing ?? document.createElement("div");
        loader.className = "admin-cms-template-media-loader";
        loader.setAttribute("role", "status");
        loader.setAttribute("aria-live", "polite");
        loader.innerHTML = `<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i><span>${this.escapeHtml(message)}</span>`;
        if (!existing) host.appendChild(loader);
    }

    static attachOverlay(field: HTMLElement, callbacks: CmsMediaOverlayCallbacks): () => void {
        const overlay = document.createElement("div");
        overlay.className = "admin-cms-template-media-overlay";
        overlay.dataset.cmsTemplateMediaOverlay = "1";
        overlay.innerHTML = `
            <button type="button" class="admin-cms-template-media-overlay__remove" data-cms-media-remove aria-label="미디어 제거">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
            <button type="button" class="admin-cms-template-media-overlay__drop" data-cms-media-pick>
                <i class="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>
                <span>드롭 또는 클릭으로 교체</span>
            </button>
        `;
        document.body.appendChild(overlay);

        const place = (): void => {
            const rect = field.getBoundingClientRect();
            const host = field.closest<HTMLElement>(".admin-cms-builder__center")
                ?? field.closest<HTMLElement>("[data-cms-canvas-host]")
                ?? field.closest<HTMLElement>("[data-cms-live-root]");
            const hostRect = host?.getBoundingClientRect();
            const bounds = {
                left: Math.max(0, hostRect?.left ?? 0),
                top: Math.max(0, hostRect?.top ?? 0),
                right: Math.min(window.innerWidth, hostRect?.right ?? window.innerWidth),
                bottom: Math.min(window.innerHeight, hostRect?.bottom ?? window.innerHeight),
            };
            const left = Math.max(rect.left, bounds.left);
            const top = Math.max(rect.top, bounds.top);
            const right = Math.min(rect.right, bounds.right);
            const bottom = Math.min(rect.bottom, bounds.bottom);
            const width = Math.max(0, right - left);
            const height = Math.max(0, bottom - top);

            overlay.hidden = rect.width <= 0 || rect.height <= 0 || width <= 0 || height <= 0;
            if (overlay.hidden) return;

            overlay.style.left = `${left}px`;
            overlay.style.top = `${top}px`;
            overlay.style.width = `${width}px`;
            overlay.style.height = `${height}px`;
        };

        const stop = (event: Event): void => {
            event.preventDefault();
            event.stopPropagation();
        };

        const dropFile = async (event: DragEvent): Promise<void> => {
            stop(event);
            overlay.classList.remove("is-dragover");
            const file = event.dataTransfer?.files?.[0] ?? null;
            if (!file) return;
            const category = detectCategoryFromFile(file);
            if (category !== "image" && category !== "video") {
                await Modal.alert("이미지 또는 영상 파일만 업로드할 수 있습니다.");
                return;
            }
            await callbacks.onDrop(file);
        };

        overlay.querySelector<HTMLElement>("[data-cms-media-pick]")?.addEventListener("click", async (event) => {
            stop(event);
            await callbacks.onPick();
        });
        overlay.querySelector<HTMLElement>("[data-cms-media-remove]")?.addEventListener("click", async (event) => {
            stop(event);
            await callbacks.onRemove();
        });
        overlay.addEventListener("click", (event) => stop(event));
        overlay.addEventListener("dragenter", (event) => {
            stop(event);
            overlay.classList.add("is-dragover");
        });
        overlay.addEventListener("dragover", (event) => {
            stop(event);
            overlay.classList.add("is-dragover");
        });
        overlay.addEventListener("dragleave", (event) => {
            stop(event);
            if (!overlay.contains(event.relatedTarget as Node | null)) {
                overlay.classList.remove("is-dragover");
            }
        });
        overlay.addEventListener("drop", (event) => {
            void dropFile(event);
        });

        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        place();

        return () => {
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
            overlay.remove();
        };
    }

    private static loaderHost(field: HTMLElement): HTMLElement {
        if (field instanceof HTMLImageElement || field instanceof HTMLVideoElement) {
            return field.parentElement ?? field;
        }
        return field;
    }

    private static escapeHtml(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }
}
